<template>
  <div class="relative my-10 flex flex-col sm:my-12">
    <div
      v-if="instructions"
      class="max-w-text text-gray-300 typo-body-sm md:typo-body-md"
      v-html="instructions"
    />
    <ul class="flex flex-wrap gap-10" :class="{ 'mt-6': instructions }">
      <li v-for="(option, index) in computedOptions" :key="index">
        <label
          class="checkbox-container relative block cursor-pointer select-none pl-8"
          :for="option.value"
        >
          <span class="text-typo-sm capitalize" v-html="option.label" />
          <Field
            :id="option.value"
            :value="option.value"
            :name="name || handle"
            :label="label"
            :disabled="disabled"
            :rules="rules"
            :model-value="modelValue"
            type="checkbox"
            class="peer absolute left-0 z-0 translate-y-0.5 cursor-pointer opacity-0"
            @update:model-value="$emit('update:model-value', $event)"
          />

          <span
            class="checkbox-checkmark peer-focus-visible:ring-offset-anthrazit absolute inset-x-0 top-0 mt-1 block size-5 shrink-0 translate-y-[-0.1em] border border-gray-300 bg-beige-200 duration-100 ease-in-out peer-focus-visible:ring-2 peer-focus-visible:ring-blue-300 peer-focus-visible:ring-offset-2"
          />
        </label>
      </li>
    </ul>

    <!-- ERROR -->
    <Collapse v-slot="{ ref }" :open="!!error">
      <p :ref="ref" class="block pt-3 text-typo-sm text-notification-error">
        {{ error }}
      </p>
    </Collapse>
  </div>
</template>

<script lang="ts" setup>
import { Field } from 'vee-validate'

defineEmits(['update:model-value'])

const props = defineProps({
  handle: {
    type: String,
    default: null,
  },

  name: {
    type: String,
    default: null,
  },

  instructions: {
    type: String,
    default: null,
  },

  options: {
    type: [Array, Object],
    default: () => [],
  },

  rules: {
    type: [Array, String],
    default: '',
  },

  error: {
    type: String,
    default: null,
  },

  disabled: {
    type: Boolean,
    default: false,
  },

  label: {
    type: String,
    default: '',
  },

  modelValue: {
    type: Array,
    default: () => [],
  },
})

const computedOptions = computed(() => {
  if (Array.isArray(props.options)) {
    return props.options.map((option) => {
      if (typeof option === 'string') {
        return {
          label: option,
          value: option,
        }
      }

      return option
    })
  }

  return Object.keys(props.options).map((key) => ({
    label: (props.options as Record<string, any>)[key],
    value: key,
  }))
})
</script>

<style lang="css" scoped>
.checkbox-checkmark {
  @apply transition-all duration-200 ease-out;

  &::after {
    @apply absolute size-3 origin-bottom -translate-x-1 -rotate-45 opacity-0 transition-transform duration-0;
    content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12.023 9.688'%3E%3Cpath id='Path_556' data-name='Path 556' d='M-23312.98-6465.871l2.719,3.312,3.762-4.221,2.723-3.055' transform='translate(23314.389 6471.247)' fill='none' stroke='%23fff' stroke-linecap='round' stroke-linejoin='round' stroke-width='2'/%3E%3C/svg%3E%0A");
    top: -0.275rem;
    left: 0.1875rem;
  }
}

.checkbox-container input:checked ~ .checkbox-checkmark {
  @apply bg-gray-300;

  &::after {
    /* @apply opacity-100 transition-transform duration-75 ease-in; */
    @apply translate-x-0 translate-y-0 rotate-0 opacity-100 transition-all delay-75 duration-200 ease-in;
  }
}
</style>
